import type React from 'react';
import { useEffect, useId, useRef } from 'react';

export interface PopupBlockedDialogProps {
  /** Re-attempt opening the meeting popup (runs inside this button's gesture). */
  onRetry: () => void;
  /**
   * Give up on the popup and open the meeting in the current window instead.
   * When omitted that fallback is hidden, because some hosts (the desktop app)
   * cannot afford to lose the current window.
   */
  onOpenInPage?: () => void;
  /** Dismiss the dialog without launching the meeting. */
  onCancel: () => void;
}

/**
 * Shown when the browser blocks the meeting popup in hosted (MF) mode. Offers
 * the three fallbacks Meet can safely provide without a full redesign: retry
 * the popup, open the meeting in this window, or cancel.
 *
 * TODO(SM-1135): this is a temporary, minimal fallback. Longer term we should
 * guide the user to unblock popups for the Meet origin (and/or detect the
 * blocked state pre-emptively) rather than opening the call inside the host tab.
 */
const PopupBlockedDialog: React.FC<PopupBlockedDialogProps> = ({
  onRetry,
  onOpenInPage,
  onCancel,
}) => {
  const headingId = useId();
  const descriptionId = useId();
  const retryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    retryButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [onCancel]);

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
    >
      <div className="popup-blocked-dialog">
        <div className="popup-blocked-dialog__content">
          <h2 id={headingId} className="popup-blocked-dialog__title">
            Allow pop-ups to start your meeting
          </h2>
          <p id={descriptionId} className="popup-blocked-dialog__message">
            Your browser blocked the meeting window. Allow pop-ups for this site and try again
            {onOpenInPage ? ', or open the meeting in this window instead' : ''}.
          </p>
          <div className="popup-blocked-dialog__actions">
            <button
              type="button"
              className="popup-blocked-dialog__button popup-blocked-dialog__button--tertiary"
              onClick={onCancel}
            >
              Cancel
            </button>
            {onOpenInPage ? (
              <button
                type="button"
                className="popup-blocked-dialog__button popup-blocked-dialog__button--secondary"
                onClick={onOpenInPage}
              >
                Open in this window
              </button>
            ) : null}
            <button
              ref={retryButtonRef}
              type="button"
              className="popup-blocked-dialog__button popup-blocked-dialog__button--primary"
              onClick={onRetry}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PopupBlockedDialog };
